import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { Dropdown } from 'react-bootstrap';
import { SUPPORTED_LOCALES } from '../../i18n/config';
import { localeHomePath } from '../../constants/siteSeo';
import { getLocaleSwitchNavigatePath } from '../../utils/localePath';

/** 利用可能な言語（表示順・common.language_* キー）。SUPPORTED_LOCALES と一致させること。 */
const languages = [
  { code: 'ja', translationKey: 'common.language_ja' },
  { code: 'en', translationKey: 'common.language_en' },
  { code: 'zh-CN', translationKey: 'common.language_zh_cn' },
  { code: 'fr', translationKey: 'common.language_fr' },
  { code: 'es', translationKey: 'common.language_es' },
  { code: 'de', translationKey: 'common.language_de' },
  { code: 'pt', translationKey: 'common.language_pt' },
  { code: 'ru', translationKey: 'common.language_ru' },
];

type LanguageSwitcherProps = {
  /** 予約共有バーなど狭い帯向け（短いラベル・リンク見た目） */
  compact?: boolean;
};

export default function LanguageSwitcher({ compact = false }: LanguageSwitcherProps) {
  const { i18n, t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const changeLanguage = (lng: string) => {
    i18n.changeLanguage(lng);
    // localStorageとCookieにも保存（他のページで引き継ぐため）
    localStorage.setItem('i18nextLng', lng);
    localStorage.setItem('preferred_language', lng);
    document.cookie = `preferred_language=${lng}; max-age=${30 * 24 * 60 * 60}; path=/`;

    const pathname = location.pathname;
    const isTop = pathname === '/' || pathname === '' || SUPPORTED_LOCALES.some((loc) => pathname === `/${loc}`);
    if (isTop) {
      // ja 正規は `/`（`/ja` ではない）
      navigate(localeHomePath(lng), { replace: true });
    } else {
      const nextPath = getLocaleSwitchNavigatePath(pathname, lng);
      if (nextPath) {
        navigate(nextPath, { replace: true });
      }
    }
  };

  const currentLanguage = languages.find((lang) => lang.code === i18n.language) || languages[0];

  // 言語名のマッピング（翻訳が見つからない場合のフォールバック）
  const languageNames: Record<string, string> = {
    'ja': '日本語',
    'en': 'English',
    'zh-CN': '中文（简体）',
    'fr': 'Français',
    'es': 'Español',
    'de': 'Deutsch',
    'pt': 'Português',
    'ru': 'Русский',
  };

  const compactLabels: Record<string, string> = {
    ja: 'JA',
    en: 'EN',
    'zh-CN': 'ZH',
    fr: 'FR',
    es: 'ES',
    de: 'DE',
    pt: 'PT',
    ru: 'RU',
  };

  const label = compact
    ? compactLabels[currentLanguage.code] || currentLanguage.code.toUpperCase()
    : currentLanguage
      ? t(currentLanguage.translationKey, {
          defaultValue: languageNames[currentLanguage.code] || currentLanguage.code,
        })
      : 'Language';

  return (
    <Dropdown>
      <Dropdown.Toggle
        variant={compact ? 'link' : 'outline-secondary'}
        size={compact ? 'sm' : undefined}
        id={compact ? 'language-dropdown-compact' : 'language-dropdown'}
        className={compact ? 'text-decoration-none' : undefined}
        aria-label={t('common.language', { defaultValue: 'Language' })}
        title={t('common.language_switch_title', {
          defaultValue: '表示言語を切り替える',
        })}
      >
        {compact ? (
          label
        ) : (
          <>
            <i className="fas fa-globe me-1" aria-hidden="true" />
            {label}
          </>
        )}
      </Dropdown.Toggle>
      <Dropdown.Menu variant={compact ? 'dark' : undefined} align="end">
        {languages.map((lang) => (
          <Dropdown.Item
            key={lang.code}
            onClick={() => changeLanguage(lang.code)}
            active={i18n.language === lang.code}
            data-value={lang.code}
          >
            {t(lang.translationKey, { defaultValue: languageNames[lang.code] || lang.code })}
          </Dropdown.Item>
        ))}
      </Dropdown.Menu>
    </Dropdown>
  );
}

